import { Box, Button } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import useAPI from "../../api/hooks/useAPI";
import { useCheckUserPermission } from "../../api/hooks/useCheckUserPermission";
import useColors from "../../api/hooks/useColors";
import {
  controlKeys,
  remediationAuditLogKeys,
  remediationKeys,
} from "../../api/queryKeys";
import SvgToIcon from "../../icons/SvgToIcon";
import { icons } from "../../scenes/stack/environment/icons/iconExport";
import { PLATFORM_USER_ACTIONS } from "../../utils/constants";
import CustomToolTipWithPermission from "../CustomToolTipWithPermission";
import ConfirmationDialog from "../dialogs/ConfirmationDialog";
import { Toast } from "../Toast";

export default function RevokeAtlassianAccessUserButton({
  controlId,
  userId = null,
  resourceIds = [],
  showToolTip = true,
  disabled = false,
  ...props
}) {
  const colors = useColors();
  const queryClient = useQueryClient();
  const { revokeAtlassianUserAccess, trackEvent } = useAPI();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { permitted: hasPermission } = useCheckUserPermission(
    PLATFORM_USER_ACTIONS.REVOKE_ATLASSIAN_USER_ACCESS
  );

  const handleRevoke = async () => {
    trackEvent("User clicked on 'Revoke Atlassian access' button");
    setIsDialogOpen(true);
  };

  const handleRevokeConfirmButton = async () => {
    trackEvent("User confirmed Atlassian access suspension");
    Toast.info(`Suspending Atlassian user access...`);

    try {
      if (resourceIds.length > 0) {
        const resourceIdsToRevoke = resourceIds.map((resourceId) => {
          const index = resourceId.indexOf("-");
          return index > -1 ? resourceId.substring(index + 1) : resourceId;
        });

        const result = await revokeAtlassianUserAccess(
          controlId,
          userId,
          resourceIdsToRevoke
        );
        const { success = [], failed = [] } = result?.data || {};
        const total = success.length + failed.length;
        const succeeded = success.length;
        const failedCount = failed.length;

        if (succeeded === total) {
          Toast.success(
            `Successfully suspended user access to all Atlassian products`
          );
        } else if (succeeded > 0) {
          Toast.info(
            `Suspended access to ${succeeded}/${total} tracked resources. Failed for ${failedCount}.`
          );
        } else {
          Toast.error("Unable to suspend Atlassian user access.");
        }

        queryClient.invalidateQueries({ queryKey: controlKeys.all });
        queryClient.invalidateQueries({
          queryKey: remediationAuditLogKeys.auditLogs(controlId),
        });
        queryClient.invalidateQueries({ queryKey: remediationKeys.all });
      }
    } catch (error) {
      Toast.error("Failed to suspend Atlassian user access");
    } finally {
      handleCloseDialog();
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  return (
    <>
      <CustomToolTipWithPermission
        title={
          showToolTip ? "Suspend user access to release product licenses" : ""
        }
        permission={hasPermission}
        placement="top"
      >
        <Box>
          <Button
            sx={{
              color: colors.text_primary,
              backgroundColor: colors.common_white,
              width: "32px",
              height: "32px",
              minWidth: "auto",
              border: "1px solid " + colors.enabled_stroke,
              textTransform: "unset",
              borderRadius: "8px",
              cursor: "pointer",
              ...props.sx,
            }}
            onClick={handleRevoke}
            disabled={disabled || !hasPermission}
          >
            {/* TODO: Replace with a suitable Atlassian icon */}
            <SvgToIcon
              IconLogo={icons.RevokeTokenIcon}
              style={{
                width: "18px",
                color:
                  disabled || !hasPermission
                    ? colors.action_disabled
                    : colors.icon_action_active,
              }}
            />
          </Button>
        </Box>
      </CustomToolTipWithPermission>

      <ConfirmationDialog
        openDialog={isDialogOpen}
        handleDialogClose={handleCloseDialog}
        handleConfirm={handleRevokeConfirmButton}
        dialogTitle="Suspend Atlassian user access"
        dialogContentFirstLine="Are you sure you want to suspend this user's access to ALL Atlassian products? This action will temporarily remove their access to all products in the organization."
        dialogContentSecondLine="The user will lose access to all Atlassian products. They will retain their roles and group memberships if access is later restored."
        confirmationButtonText="Suspend Access"
        destructive={true}
      />
    </>
  );
}
